import { AxiosError } from 'axios'
import { computed, flow, makeObservable, observable, toJS } from 'mobx'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, SortingParams } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import { prepareGenericParams } from 'Core/utils'
import { storagePageSizeConstant } from 'Core/utils/storeHelpers/storagePageSizeConstant'

import { Location } from 'Portals/Customer/entities'
import { LocationRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

const STORAGE_BACKUP_DOCUMENT_PAGE_SIZE = storagePageSizeConstant('dataEntryByLocationTable')

export class LocationTable extends TableStore<Location.Basic> {
	@observable initialScrollY: number = 0

	constructor(private rootStore: RootStore) {
		super(STORAGE_BACKUP_DOCUMENT_PAGE_SIZE)
		makeObservable(this)
	}

	@computed
	get locations() {
		return toJS(this.items)
	}

	getLocationSelectedRowKeys(data: Location.Basic[]) {
		return data.reduce((acc, item) => {
			if (item.isSelected) {
				acc.push(item.id)
			}
			return acc
		}, [] as number[])
	}

	@flow.bound
	*getItems(defaultSort?: SortingParams) {
		this.initialScrollY = 0
		this.pageNumber = 1
		try {
			yield this.requestData({
				defaultSort,
				repository: LocationRepo.getList
			})
			const selectedRowKeys = this.getLocationSelectedRowKeys(this.items)
			this.setSelectedRowKeys(selectedRowKeys)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
			throw error
		}
	}

	@flow.bound
	*addItems(): CustomGenerator<ListData<Location.Basic>> {
		const newPageNumber = this.pageNumber + 1
		try {
			const params = prepareGenericParams(this)
			const newParams = {
				...params,
				page: newPageNumber
			}
			const data = yield LocationRepo.getList(newParams)
			this.items = [...this.items, ...data.items]
			const newSelectedRowKeys = this.getLocationSelectedRowKeys(data.items)
			this.setSelectedRowKeys([...this.selectedRowKeys, ...newSelectedRowKeys] as number[])
			this.totalCount = data?.total || 0
			this.pageNumber = newPageNumber
			this.initialScrollY = 1
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}
}
